import logger from "../util/logger";
import { ReduxDispatch, ReduxGetState, ReduxState } from "./reducers";
import actionCreatorFactory from "../util/redux/action-factory";
import { reducerWithInitialState } from "../util/redux/typed-reducers";
import { env } from "../util/env";
import * as request from "../util/request";
import { LoginResult } from "util/DTOs/UserDtos";

const actionFactory = actionCreatorFactory("Hypo/EIdentify");

export const loadEIdentifyProviders = actionFactory<
    null,
    IEIdentifyProvider[],
    string
>("LOAD_EIDENTIFY_PROVIDERS");
export const loadEIdentifyRedirecturi = actionFactory<null, string, string>(
    "LOAD_EIDENTIFY_REDIRECTURI"
);
export const loadEIdentifyAuthentication = actionFactory<
    null,
    EIdentifySuccessResponse,
    string
>("LOAD_EIDENTIFY_AUTHENTICATION");

export interface IEIdentifyProvider {
    id: number;
    imageUrl: string;
    name: string;
}

export interface EIdentifySuccessResponse {
    action: number;
    tokenResponse: LoginResult | null;
    hash: string | null;
    customerId: string | null;
    customerName: string | null;
}

export interface EIdentifyState {
    providerInfo: IEIdentifyProvider[] | undefined;
    loading: boolean;
    error: string | undefined;
    redirectUri: string | undefined;
    loginData: EIdentifySuccessResponse | undefined;
}

const initialEIdentifyState: EIdentifyState = {
    providerInfo: undefined,
    loading: false,
    error: undefined,
    redirectUri: undefined,
    loginData: undefined,
};

export function startLoadingEIdentifyProviders() {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(loadEIdentifyProviders(null));
            const providerInfo = await request.get<IEIdentifyProvider[]>(
                `${env.APP_API_HOST}/api/eidentify`
            );
            dispatch(loadEIdentifyProviders.done(providerInfo));
        } catch (error) {
            dispatch(
                loadEIdentifyProviders.failed("Failed to load provider data")
            );
        }
    };
}

export function startLoadingEIdentifyRedirectUri(
    requestType: number,
    id: number
) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(loadEIdentifyRedirecturi(null));
            const redirectUri = await request.get<string>(
                `${env.APP_API_HOST}/api/eidentify/${requestType}/${id}`
            );
            dispatch(loadEIdentifyRedirecturi.done(redirectUri));
        } catch (error) {
            dispatch(
                loadEIdentifyRedirecturi.failed("Failed to load redirect uri")
            );
        }
    };
}

export function startEIdentifyAuthentication(code: string, action: string) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(loadEIdentifyAuthentication(null));
            const res = await request.post<EIdentifySuccessResponse>(
                `${env.APP_API_HOST}/api/eidentify`,
                { code, action }
            );
            dispatch(loadEIdentifyAuthentication.done(res));
        } catch (error) {
            dispatch(
                loadEIdentifyAuthentication.failed(
                    "Failed to eidentify authenticate"
                )
            );
        }
    };
}

export const EIdentifyReducer = reducerWithInitialState(initialEIdentifyState)
    .case(loadEIdentifyProviders, (state, action) => {
        // NOTE: TUPAS will do a roundtrip on providers portal so state will be reloaded.
        // That's why we want the redirect url to be saved on session also so that we can redirect
        // back to previous content after tupas is done.
        //sessionStorage.setItem("redirectPath", action.payload.tupasRedirectPath || "/");
        //sessionStorage.setItem("errorReturnPath", action.payload.tupasErrorReturnPath || "/");
        return {
            ...state,
            //tupasRedirectPath: action.payload.tupasRedirectPath,
            //tupasErrorReturnPath: action.payload.tupasErrorReturnPath,
            loading: true,
            tupasActionResponse: undefined,
        };
    })
    .case(loadEIdentifyProviders.done, (state, action) => {
        return {
            ...state,
            providerInfo: action.payload,
            loading: false,
            error: undefined,
            loginData: undefined,
        };
    })
    .case(loadEIdentifyProviders.failed, (state, action) => {
        return {
            ...state,
            loading: false,
            error: action.payload,
        };
    })
    .case(loadEIdentifyRedirecturi, (state, action) => {
        return {
            ...state,
            loading: true,
            redirectUri: undefined,
        };
    })
    .case(loadEIdentifyRedirecturi.done, (state, action) => {
        return {
            ...state,
            loading: false,
            redirectUri: action.payload,
        };
    })
    .case(loadEIdentifyRedirecturi.failed, (state, action) => {
        return {
            ...state,
            loading: false,
            error: action.payload,
        };
    })
    .case(loadEIdentifyAuthentication, (state, action) => {
        return {
            ...state,
            loading: true,
            error: undefined,
            loginData: undefined,
        };
    })
    .case(loadEIdentifyAuthentication.done, (state, action) => {
        return {
            ...state,
            loading: false,
            error: undefined,
            loginData: action.payload,
        };
    })
    .case(loadEIdentifyAuthentication.failed, (state, action) => {
        return {
            ...state,
            loading: false,
            error: action.payload,
        };
    });

export function getEIdentifyProviders(
    state: ReduxState
): IEIdentifyProvider[] | null {
    return (state.eidentify && state.eidentify.providerInfo) || null;
}

export function getEIdentifyRedirectUri(state: ReduxState): string | null {
    return (state.eidentify && state.eidentify.redirectUri) || null;
}

export function getEIDentifyLoading(state: ReduxState): boolean {
    return (state.eidentify && state.eidentify.loading) || false;
}

export function getEIdentifyLoginData(
    state: ReduxState
): EIdentifySuccessResponse | null {
    return (state.eidentify && state.eidentify.loginData) || null;
}

export default EIdentifyReducer;



// WEBPACK FOOTER //
// ./src/ducks/eidentify.ts